import { gql } from '@apollo/client';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconCollapseList from '@apollo/icons/default/IconCollapseList.svg';
import IconExpandList from '@apollo/icons/default/IconExpandList.svg';
import IconSandbox from '@apollo/icons/default/IconSandbox.svg';
import { Button } from '@apollo/orbit';
import { Rocket2 } from '@apollo/space-kit/illustrations/Rocket2';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { countAddedAndDeletedTypes } from 'src/lib/graphql/graphqlHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  LintDiagnosticLevel,
  ViolationLevel,
} from 'src/lib/graphqlTypes/types';

import { TypeAndFieldDiffCount } from '../typeAndFieldDiffCount/TypeAndFieldDiffCount';

import {
  CustomCheckViolationListFragment,
  OverrideViolationButtonIgnoredRuleFragment,
  RuleViolationList,
  RuleViolationListLintDiagnosticFragment,
  RuleViolationListLinterRuleLevelConfigurationFragment,
  Violation,
} from './ruleViolationList/RuleViolationList';

export {
  OverrideViolationButtonIgnoredRuleFragment,
  CustomCheckViolationListFragment,
  RuleViolationListLintDiagnosticFragment,
};

export const LintCheckListCheckWorkflowFragment = gql`
  fragment LintCheckListCheckWorkflowFragment on CheckWorkflow {
    id
    isSandboxCheck
    baseVariant {
      id
      hasSupergraphSchema
    }
    proposedSchemaHash
    proposedSubgraphs {
      hash
      name
    }
  }
`;

export const LintCheckListLinterRuleLevelConfigurationFragment = gql`
  fragment LintCheckListLinterRuleLevelConfigurationFragment on LinterRuleLevelConfiguration {
    rule
    ...RuleViolationListLinterRuleLevelConfigurationFragment
  }
  ${RuleViolationListLinterRuleLevelConfigurationFragment}
`;

export const LintCheckList = ({
  violationsBySubgraph,
  workflow,
  documentLoading,
  rules,
  ignoredLinterViolations,
  selectedSchema,
  typeAndFieldCount,
  graphId,
  showMessage,
}: {
  violationsBySubgraph: Record<string, Array<Violation>>;
  workflow: GraphQLTypes.LintCheckListCheckWorkflowFragment;
  documentLoading: boolean;
  rules: GraphQLTypes.LintCheckListLinterRuleLevelConfigurationFragment[];
  ignoredLinterViolations:
    | GraphQLTypes.OverrideViolationButtonIgnoredRuleFragment[]
    | undefined;
  selectedSchema: string;
  typeAndFieldCount: ReturnType<typeof countAddedAndDeletedTypes> | undefined;
  graphId: string;
  showMessage: boolean;
}) => {
  const isSupergraph = !!workflow.baseVariant?.hasSupergraphSchema;

  const groupedResultsBySubgraph = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(violationsBySubgraph).map((key) => [
          key,
          _.partition(
            violationsBySubgraph[key]
              ?.filter(
                (summary): summary is NonNullable<typeof summary> => !!summary,
              )
              .sort((a, b) => a.rule.localeCompare(b.rule)),
            (violation) =>
              violation.level === LintDiagnosticLevel.ERROR ||
              violation.level === ViolationLevel.ERROR,
          ).map((violations) =>
            _.groupBy(violations, (violation) => violation.rule),
          ),
        ]),
      ),
    [violationsBySubgraph],
  );

  const getInitialCollapsedListsBySubgraph = useCallback(() => {
    return Object.fromEntries(
      Object.entries(groupedResultsBySubgraph).map(
        ([subgraphName, groupedResultsForSubgraph]) => [
          subgraphName,
          new Set([
            ...Object.keys(groupedResultsForSubgraph[0] ?? {}),
            ...Object.keys(groupedResultsForSubgraph[1] ?? {}),
          ]),
        ],
      ),
    );
  }, [groupedResultsBySubgraph]);

  const [collapsedListsBySubgraph, setCollapsedListsBySubgraph] = useState(
    getInitialCollapsedListsBySubgraph(),
  );
  const onCollapseAll = useCallback(() => {
    setCollapsedListsBySubgraph(getInitialCollapsedListsBySubgraph());
  }, [getInitialCollapsedListsBySubgraph]);

  const onCollapseSingleList = useCallback(
    (ruleName: string) => {
      const newStateForSubgraph = new Set(
        collapsedListsBySubgraph[selectedSchema],
      );
      if (newStateForSubgraph.has(ruleName)) {
        newStateForSubgraph.delete(ruleName);
      } else {
        newStateForSubgraph.add(ruleName);
      }
      setCollapsedListsBySubgraph((prev) => ({
        ...prev,
        [selectedSchema]: newStateForSubgraph,
      }));
    },
    [collapsedListsBySubgraph, selectedSchema],
  );

  if (documentLoading) {
    return <Loading />;
  }

  const groupedResultsForSubgraph = groupedResultsBySubgraph[selectedSchema];
  if (!groupedResultsForSubgraph) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <div>
          <Rocket2 className="mx-auto mb-6 block" />
          <div className="mb-6 text-center text-xl font-semibold">
            No lint issues in this subgraph!
          </div>
        </div>
      </div>
    );
  }

  const [groupedErrorResults, groupedWarningResults] =
    groupedResultsForSubgraph;
  const collapsedLists = collapsedListsBySubgraph[selectedSchema];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {workflow.isSandboxCheck && (
            <>
              <IconSandbox className="text-icon-primary" />
              <span className="ml-2">Sandbox check</span>
              <IconChevronRight
                className="mx-2 text-secondary"
                role="presentation"
              />
            </>
          )}
          {typeAndFieldCount && (
            <TypeAndFieldDiffCount
              modifiedSchemaTypeCount={
                typeAndFieldCount.modifiedSchemaTypeCount
              }
              modifiedSchemaFieldCount={
                typeAndFieldCount.modifiedSchemaFieldCount
              }
              fieldsAdded={typeAndFieldCount.fieldsAdded}
              fieldsRemoved={typeAndFieldCount.fieldsRemoved}
              typesAdded={typeAndFieldCount.typesAdded}
              typesRemoved={typeAndFieldCount.typesRemoved}
            />
          )}
        </div>
        <div className="space-x-2">
          {collapsedListsBySubgraph[selectedSchema]?.size === 0 ? (
            <Button
              variant="hidden"
              leftIcon={<IconCollapseList />}
              onClick={onCollapseAll}
            >
              Collapse All
            </Button>
          ) : (
            <Button
              variant="hidden"
              leftIcon={<IconExpandList />}
              onClick={() =>
                setCollapsedListsBySubgraph((prev) =>
                  Object.fromEntries(
                    Object.keys(prev).map((key) => [key, new Set()]),
                  ),
                )
              }
            >
              Expand All
            </Button>
          )}
        </div>
      </div>
      <div className="mt-4 space-y-4">
        {Object.entries(groupedErrorResults ?? {}).map(
          ([ruleName, violations]) => (
            <RuleViolationList
              key={ruleName}
              violations={violations}
              name={ruleName}
              isCollapsed={collapsedLists?.has(ruleName)}
              setIsCollapsed={() => onCollapseSingleList(ruleName)}
              canIgnoreLines
              isSupergraph={isSupergraph}
              subgraphName={selectedSchema}
              rule={rules.find((rule) => rule.rule === ruleName)}
              ignoredLinterViolations={ignoredLinterViolations}
              proposedSubgraphs={workflow.proposedSubgraphs}
              proposedSupergraphSchema={{
                hash: workflow.proposedSchemaHash,
              }}
              graphId={graphId}
              showMessage={showMessage}
            />
          ),
        )}
        {Object.entries(groupedWarningResults ?? {}).map(
          ([ruleName, violations]) => (
            <RuleViolationList
              key={ruleName}
              violations={violations}
              name={ruleName}
              isCollapsed={collapsedLists?.has(ruleName)}
              setIsCollapsed={() => onCollapseSingleList(ruleName)}
              canIgnoreLines
              isSupergraph={isSupergraph}
              subgraphName={selectedSchema}
              rule={rules.find((rule) => rule.rule === ruleName)}
              ignoredLinterViolations={ignoredLinterViolations}
              proposedSubgraphs={workflow.proposedSubgraphs}
              proposedSupergraphSchema={{
                hash: workflow.proposedSchemaHash,
              }}
              graphId={graphId}
              showMessage={showMessage}
            />
          ),
        )}
      </div>
    </div>
  );
};
